import { Stack } from '@mui/material'
import type { FC } from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { MasterSelector } from 'src/features/templateEdit/components/MasterSelector/MasterSelector'
import { hasNodeMasterFieldReference } from 'src/features/templateEdit/utils/hasNodeMasterFieldReference'
import approvalFlowsOperations from 'src/state/ducks/approvalFlows/operations'
import placesOperations from 'src/state/ducks/places/operations'
import {
  setMasterErrorMessage,
  toggleMasterEnabled,
  updateMasterUuid,
} from 'src/state/ducks/templates/editTemplate/actions'
import type { RootState } from 'src/state/store'
import ApprovalFlowRow from './ApprovalFlowRow'
import HintRow from './HintRow'
import IconRow from './IconRow'
import LayoutRow from './LayoutRow'
import ManualRow from './ManualRow'
import { NameRow } from './NameRow'
import PlaceRow from './PlaceRow'
import ReportNamingRuleRow from './ReportNamingRuleRow'
import SwitchRow from './SwitchRow'

const EditTemplateOption: FC = () => {
  const approvalFlows = useSelector(
    (state: RootState) => state.approvalFlowsState.approvalFlows.approvalFlows,
    shallowEqual,
  )
  const templatePlaceNodes = useSelector(
    (state: RootState) => state.placesState.placeNodes.nodes,
    shallowEqual,
  )

  const dispatch = useDispatch()

  useEffect(() => {
    approvalFlowsOperations.getApprovalFlows(dispatch)
    placesOperations.getPlaceNodes(dispatch)
  }, [dispatch])

  const masterUuid = useSelector(
    (state: RootState) => state.templatesState.editTemplate.masterUuid,
    shallowEqual,
  )

  const isMasterEnabled = useSelector(
    (state: RootState) => state.templatesState.editTemplate.isMasterEnabled,
    shallowEqual,
  )

  const masterErrorMessage = useSelector(
    (state: RootState) => state.templatesState.editTemplate.masterErrorMessage,
    shallowEqual,
  )

  const templateNodes = useSelector(
    (state: RootState) => state.templatesState.templatePages.templateNodes,
    shallowEqual,
  )

  const hasQuestionsWithMasterFieldReferences = useMemo(() => {
    return Object.values(templateNodes).some(hasNodeMasterFieldReference)
  }, [templateNodes])

  const handleMasterUuidChange = useCallback(
    (newMasterUuid: string | null) => {
      dispatch(updateMasterUuid(newMasterUuid))
      if (newMasterUuid !== null) {
        dispatch(setMasterErrorMessage(''))
      }
    },
    [dispatch],
  )

  const handleMasterEnabledChange = useCallback(
    (newIsMasterEnabled: boolean) => {
      dispatch(toggleMasterEnabled(newIsMasterEnabled))
      if (!newIsMasterEnabled) {
        dispatch(setMasterErrorMessage(''))
      }
    },
    [dispatch],
  )

  return (
    <Stack
      spacing={2}
      py={4}
      px={2}
      sx={{ maxWidth: 900, width: '100%', mx: 'auto' }}
    >
      <NameRow />
      <MasterSelector
        value={masterUuid}
        onChange={handleMasterUuidChange}
        onToggleChange={handleMasterEnabledChange}
        isMasterEnabled={isMasterEnabled}
        hasQuestionsWithMasterFieldReferences={
          hasQuestionsWithMasterFieldReferences
        }
        externalError={masterErrorMessage}
      />
      <ReportNamingRuleRow />
      <SwitchRow />
      <IconRow />
      <LayoutRow />
      {approvalFlows.length > 0 && <ApprovalFlowRow />}
      {templatePlaceNodes && <PlaceRow />}
      <HintRow />
      <ManualRow />
    </Stack>
  )
}

export default EditTemplateOption
